import { useCallback } from 'react'

import { CopyContent, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../common/copy'

import { useLazyGetEffectiveNodeQuery, useLazyGetInputParametersHierarchyDefaultQuery } from '../../../store/ai/inputParametersHierarchy'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectProcessDocumentContact } from '../../../store/processDocument/documentUsers'
import { selectProcessDocumentFileInfo } from '../../../store/processDocument/documentInfo'
import { selectDocumentTypes } from '../../../store/processDocument/documentTypes'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { addToast } from '../../../store/toast/toastSlice'

import {
  clearDocumentSummaryResult,
  selectDocumentSummaryResult,
  selectDocumentType,
  selectSelectedDocumentType,
  setDocumentIsNotRelatedToClient,
  setDocumentLabel,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryExecutionLoading,
  setDocumentSummaryResult,
  setDocumentType,
  setDocumentTypeCalculationLoading,
  setSelectedDocumentType,
} from '../../../store/processDocument/documentSummary'
import { FileInfo } from '../../../store/media/file'

import { DocumentSummaryResultExecutionResultType, DocumentTypeCalculationResultType } from '../../../models/ProcessDocument'
import { DocumentType } from '../../../models/DocumentType'
import { AIAgentType } from '../../../models/AIAgent'

import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import {
  getEditorStateFromHtml,
  getHtmlContentFromState,
  getTextContentFromState,
} from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'

const documentTypeToContext = (documentType: DocumentType) => {
  const { id, name, detectionConfig } = documentType
  const { description, documentLabels } = detectionConfig
  return {
    id,
    name,
    description,
    documentLabels,
  }
}

export const useDocumentSummary = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const selectedDocumentType = useAppSelector(selectSelectedDocumentType)
  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)
  const documentType = useAppSelector(selectDocumentType)
  const documentTypes = useAppSelector(selectDocumentTypes)
  const contact = useAppSelector(selectProcessDocumentContact)

  const { tracked } = useTracking()

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [getInputParameterHierarchyDefault] = useLazyGetInputParametersHierarchyDefaultQuery()

  const calculateDocumentType = useCallback(
    async (fileInfo: FileInfo) => {
      const { fileId } = fileInfo
      dispatch(setDocumentTypeCalculationLoading(true))
      try {
        const documentTypesContext = documentTypes.map(documentTypeToContext)
        if (!documentTypesContext) {
          return null
        }
        const { data: aiAgentResult } = await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_TYPE_CALCULATION,
            context: {
              documentTypes: documentTypesContext,
            },
            attachments: [{ fileId }],
          },
        })
        if (!aiAgentResult) {
          return null
        }
        const { result } = aiAgentResult as DocumentTypeCalculationResultType
        const { typeId, label } = result

        dispatch(setDocumentLabel(label))

        const documentType = documentTypes.find((e) => e.id === typeId)
        if (!documentType) {
          return null
        }

        dispatch(setDocumentType(documentType))
        dispatch(setSelectedDocumentType(documentType))

        return documentType
      } catch (e) {
        console.error(e)
        return null
      } finally {
        dispatch(setDocumentTypeCalculationLoading(false))
      }
    },
    [accountId, dispatch, documentTypes, triggerExecuteAiAgent],
  )

  const fetchDocumentTypeTemplate = useCallback(
    async (documentType: DocumentType): Promise<string> => {
      const template = await getInputParameterHierarchyNode(
        {
          accountId,
          type: InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE,
          typeId: 'summary-template',
          context: { effective: true, userId: currentUserId, documentTypeId: documentType.id, contextType: 'document_type' },
        },
        true,
      ).unwrap()

      if (template) {
        return template.inputParameters['template'] ?? ''
      }

      const defaultTemplate = await getInputParameterHierarchyDefault(
        {
          accountId,
          type: InputParameterHierarchyType.DOCUMENT_SUMMARY_TEMPLATE,
          typeId: documentType.systemDocumentTypeId ?? 'default-summary',
        },
        true,
      ).unwrap()

      if (defaultTemplate) {
        return defaultTemplate.inputParameters['template'] ?? ''
      }

      return ''
    },
    [accountId, currentUserId, getInputParameterHierarchyDefault, getInputParameterHierarchyNode],
  )

  const summarizeDocument = useCallback(
    async (documentType: DocumentType, fileInfo: FileInfo) => {
      if (!contact) {
        return null
      }

      const { id: contactId, name: contactName } = contact
      const { id: documentTypeId } = documentType
      const { fileId } = fileInfo

      dispatch(setDocumentSummaryExecutionLoading(true))
      dispatch(clearDocumentSummaryResult())
      try {
        const template = await fetchDocumentTypeTemplate(documentType)
        const { data: aiAgentResult } = await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: AIAgentType.DOCUMENT_SUMMARY,
            context: {
              contactId,
              contactName,
              documentTypeId,
              fileId,
              template,
            },
            attachments: [{ fileId }],
          },
        })
        if (!aiAgentResult) {
          return null
        }
        const { result, aiAgentId } = aiAgentResult as DocumentSummaryResultExecutionResultType
        dispatch(setDocumentSummaryAiAgentId(aiAgentId))
        const { summary, documentIsNotRelatedToClient } = result
        dispatch(setDocumentSummaryResult({ summary }))
        if (documentIsNotRelatedToClient) {
          dispatch(setDocumentIsNotRelatedToClient(true))
        }
        return result
      } catch (e) {
        console.error(e)
        return null
      } finally {
        dispatch(setDocumentSummaryExecutionLoading(false))
      }
    },
    [accountId, contact, dispatch, fetchDocumentTypeTemplate, triggerExecuteAiAgent],
  )

  const _handleRerun = useCallback(async () => {
    if (!selectedDocumentType || !fileInfo) {
      return
    }

    const documentSummaryResult = await summarizeDocument(selectedDocumentType, fileInfo)
    if (!documentSummaryResult) {
      return
    }

    dispatch(setDocumentType(selectedDocumentType))
  }, [dispatch, fileInfo, selectedDocumentType, summarizeDocument])

  const handleRerun = tracked(
    _handleRerun,
    { eventName: TrackEvent.DOCUMENT_SUMMARY_TYPE_CHANGED },
    { condition: !!selectedDocumentType && !!documentType && selectedDocumentType.id !== documentType.id },
  )

  const getCopyContent = useCallback(() => {
    const { summary = '' } = documentSummaryResult ?? {}

    const editorState = getEditorStateFromHtml(summary)
    const lexicalState = JSON.stringify(editorState)

    return {
      [TEXT_TYPE]: getTextContentFromState(lexicalState),
      [HTML_TYPE]: getHtmlContentFromState(lexicalState),
    }
  }, [documentSummaryResult])

  const copyContentToClipboard = useCallback(
    async (copyContent: CopyContent) => {
      await writeCopyContentToClipboard(copyContent)
      dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Copied to Clipboard!'] }))
    },
    [dispatch],
  )

  const handleCopy = useCallback(async () => {
    const copyContent = getCopyContent()
    if (!copyContent) {
      return
    }
    await copyContentToClipboard(copyContent)
  }, [copyContentToClipboard, getCopyContent])

  return { summarizeDocument, calculateDocumentType, handleCopy, handleRerun }
}
